import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
  useCallback,
  memo,
} from 'react';
import { Link } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import classNames from 'classnames';
import dataakMobileLogo from 'assets/img/green-logo.svg';
import dastanLogo from 'assets/img/dastan-logo.svg';
import { UiIcon, UiTooltip, UiConfirmDialog, UiButton } from 'ui-components';
import { ChatwootWidget } from 'components';
import { useOnClickOutside, usePermission, usePlatform } from 'hooks';
import HamburgerMenu from 'ui-components/hamburger-menu/HamburgerMenu';
import { toggleMobileSidebarMenu, toggleSidebarMenu } from 'store';
import { isAppLang, getLargeLogoURL, getSmallLogoURL, getEnv } from 'utils';
import { useTranslation } from 'react-i18next';
import { StyledLayoutSidebar } from './LayoutSidebar.styles';

const LayoutSidebar = () => {
  const { t } = useTranslation();
  const menuKey = useSelector((state) => state.pageReducer.menuKey);
  const isSidebarMenuExpanded = useSelector(
    (state) => state.pageReducer.isSidebarMenuExpanded
  );
  const lastSelectedPlatform = useSelector(
    (state) => state.pageReducer.lastSelectedPlatform
  );
  const isMobileSidebarMenuActive = useSelector(
    (state) => state.pageReducer.isMobileSidebarMenuActive
  );
  const dispatch = useDispatch();
  const [openTrialVersionModal, setOpenTrialVersionModal] = useState(false);
  const { userPermission } = usePermission();
  const isArabicLanguage = isAppLang('ar');
  const { getSettingsDefaultPlatform, getPlatforms, hasPermission } =
    usePlatform();

  const mobileMenuRef = useRef(null);

  const hasMultiPlatformPermission = hasPermission('multiPlatform');
  const hasNewsPermission = hasPermission('news');
  const hasNewspaperPermission =
    hasPermission('newspaper') && !isArabicLanguage;
  const hasInstagramPermission = hasPermission('instagram');
  const hasInstagramCommentPermission =
    hasPermission('instagramComment') && !isArabicLanguage;
  const hasTwitterPermission = hasPermission('twitter');
  const hasTelegramPermission = hasPermission('telegram');
  const hasNativePlatformsPermission =
    hasPermission('nativePlatforms') && !isArabicLanguage;

  const hasCompanyListPermission = useSelector(
    (state) =>
      state.accountReducer.currentUser?.user?.permissions?.CompanyInfoAdmin
        ?.index?.permission
  );
  const hasTinyUrlPagePermission = useSelector(
    (state) =>
      state.accountReducer.currentUser?.user?.permissions?.FrontEnd
        ?.shortUrlMenu?.permission
  );
  const hasUserListPermission = useSelector(
    (state) =>
      state.accountReducer.currentUser?.user?.permissions?.User?.index
        ?.permission
  );
  const departmentId = useSelector(
    (state) => state.accountReducer.currentUser?.user?.department_id
  );
  const hasDashboardPermission = useSelector(
    (state) =>
      state.accountReducer.currentUser?.user?.permissions?.Dashboard?.index
        ?.permission
  );
  const hasDashboardMenuPermission = useSelector(
    (state) =>
      state.accountReducer.currentUser?.user?.permissions?.FrontEnd
        ?.wallDashboardMenu?.permission
  );

  const isRootUser = () => departmentId === 1;

  useOnClickOutside(mobileMenuRef, () => {
    if (isMobileSidebarMenuActive) dispatch(toggleMobileSidebarMenu(false));
  });

  const handleClickOnMenu = () =>
    isMobileSidebarMenuActive && dispatch(toggleMobileSidebarMenu(false));

  // پرمیشن جهت دسترسی به مدیریت کاربران
  const { permission: permi_admin_users } = useMemo(
    () => userPermission('Monitoring', 'getAllUsers'),
    [userPermission]
  );

  // پرمیشن جهت دسترسی به تعیین تعداد منبع ویژه برای کاربر
  const { permission: permi_admin_vip_permission } = useMemo(
    () => userPermission('VipResourceAccess', 'store'),
    [userPermission]
  );

  // پرمیشن جهت دسترسی به تیکت ها و دسته بندی تیکت ها
  const { permission: permi_admin_support } = useMemo(
    () => userPermission('FrontEnd', 'ticketManagement'),
    [userPermission]
  );

  // پرمیشن جهت دسترسی به سرچ
  let { permission: permi_search } = useMemo(
    () => userPermission('FrontEnd', 'freeSearch'),
    [userPermission]
  );

  permi_search = permi_search && !isArabicLanguage;

  // پرمیشن به لیست آلارم
  const { permission: permi_alarm_list } = useMemo(
    () => userPermission('Alarm', 'index'),
    [userPermission]
  );

  // پرمیشن به لیست بولتن
  let { permission: permi_bulletin_list } = useMemo(
    () => userPermission('Bulletin', 'index'),
    [userPermission]
  );

  permi_bulletin_list = permi_bulletin_list && !isArabicLanguage;

  // پرمیشن به دریافت تنظیمات بولتن
  let { permission: permi_bulletin_config } = useMemo(
    () => userPermission('BulletinConfig', 'getBulletinConfig'),
    [userPermission]
  );

  permi_bulletin_config = permi_bulletin_config && !isArabicLanguage;

  // پرمیشن به لیست تگ‌ ها
  const { permission: permi_tag_index } = useMemo(
    () => userPermission('Tag', 'index'),
    [userPermission]
  );

  // پرمیشن به لیست دسته بندی منبع
  const { permission: permi_searchTag_index } = useMemo(
    () => userPermission('SearchTag', 'index'),
    [userPermission]
  );

  // پرمیشن به مقایسه
  const { permission: permi_comparison } = useMemo(
    () => userPermission('FrontEnd', 'diffPage'),
    [userPermission]
  );

  const hasBrandBaanPermission = useSelector(
    (state) =>
      state.accountReducer.currentUser?.user?.permissions?.Chart?.index
        ?.permission
  );

  const hasAiChatbotPermission = useSelector(
    (state) =>
      !!state.accountReducer.currentUser?.user?.permissions?.Variables
        ?.ai_chatbot?.permission
  );

  const token = useSelector(
    (state) => state.accountReducer.currentUser?.access_token || ''
  );

  const handleChatbotMenuClick = useCallback(async () => {
    window.open(
      `${getEnv('REACT_APP_CHATBOT_URL')}/auth/callback?token=${token}&from=${
        window.location.origin
      }`,
      '_blank'
    );
  }, [token]);

  const userEmail = useSelector(
    (state) => state.accountReducer.currentUser?.user?.email
  );

  const hasBrandBuilderPermission =
    userEmail === getEnv('REACT_APP_BRAND_BUILDER_ACCOUNT');

  // باز و بسته کردن زیر منو ها
  const [subMenuState, setSubMenuState] = useState({ name: '', isOpen: false });

  useEffect(() => {
    setSubMenuState({ name: subMenuState.name, isOpen: true });
  }, [isSidebarMenuExpanded, isMobileSidebarMenuActive]);

  /**
   * باز کردن منو انتخابی و بستن زیر منو های منو های دیگر
   */
  const handleSubMenuState = (menuName) => {
    setSubMenuState({
      name: menuName,
      isOpen: subMenuState.name !== menuName ? true : !subMenuState.isOpen,
    });
  };

  /**
   * اکتیو کردن منو ها
   */
  const isActive = useCallback(
    (...keys) => {
      let _isActive = null;
      keys.forEach((key) => {
        if (menuKey === key) {
          _isActive = 'active';
        }
      });
      return _isActive;
    },
    [menuKey]
  );

  /**
   * وقتی صفحه رفرش میشه باید منو صفحه جاری اکتیو بشه
   * و زیر منو باز بشه
   */
  useEffect(() => {
    switch (menuKey) {
      case 'searchPage':
      case 'newsAndInformation':
      case 'statisticsAndCharts':
      case 'recource':
        setSubMenuState({ name: 'search', isOpen: true });
        break;
      case 'comparisonList':
      case 'comparisonAdd':
      case 'ComparisonViewSubject':
      case 'ComparisonViewSource':
        setSubMenuState({ name: 'comparison', isOpen: true });
        break;
      case 'bulletinList':
      case 'bulletinSettings':
      case 'bulletinArchive':
        setSubMenuState({ name: 'bulletin', isOpen: true });
        break;
      case 'alarm':
      case 'alarmContacts':
        setSubMenuState({ name: 'alarm', isOpen: true });
        break;
      case 'adminUsers':
      case 'adminSupport':
      case 'adminSupportCategory':
      case 'brandBuilder':
      case 'industryBuilder':
      case 'adminVipPermission':
        setSubMenuState({ name: 'admin', isOpen: true });
        break;
      default:
        break;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [menuKey]);

  const handleToggleMenu = () => {
    dispatch(toggleSidebarMenu());
  };

  const isSubMenuOpen = (menuName) => {
    return !!(subMenuState.name === menuName && subMenuState.isOpen);
  };

  const statisticsPlatform = getPlatforms('statistics').includes(
    lastSelectedPlatform
  )
    ? lastSelectedPlatform
    : getSettingsDefaultPlatform('statistics');

  const chatToken = Boolean(getEnv('REACT_APP_CHAT_WIDGET_TOKEN'));

  const hasEcommercePermission = useSelector(
    (state) =>
      state.accountReducer.currentUser?.user?.permissions?.SearchPlatforms?.news
        ?.permission
  );

  return (
    <StyledLayoutSidebar>
      {/* بازو بسته کردن منو */}
      <div
        className={classNames(
          'sidebar-toggle-btn flex',
          isSidebarMenuExpanded ? 'expanded' : 'collapsed'
        )}
        onClick={handleToggleMenu}
      >
        <span className='icon-wrapper'>
          <UiIcon
            icon={`ri-arrow-${isSidebarMenuExpanded ? 'right' : 'left'}-s-line`}
            size={32}
          />
        </span>
      </div>
      <div className='layout-sidebar-sticky' ref={mobileMenuRef}>
        <HamburgerMenu type={1} />
        <div
          className={classNames(
            'layout-sidebar',
            isMobileSidebarMenuActive ? 'animation-in' : '',
            isSidebarMenuExpanded ? '' : 'close',
            chatToken &&
              getEnv('REACT_APP_ENVIRONMENT') === 'development' &&
              'with-chat-widget'
          )}
        >
          <div className='logo flex center'>
            <Link to='/' className='flex center' data-testid='home-menu'>
              <img
                className='app-logo'
                src={
                  isSidebarMenuExpanded ? getLargeLogoURL() : getSmallLogoURL()
                }
                alt=''
              />
              <img
                className='app-logo mobile-menu-logo'
                src={
                  getEnv('REACT_APP_NAME') !== 'dataak'
                    ? getLargeLogoURL()
                    : dataakMobileLogo
                }
                alt=''
              />
            </Link>
          </div>

          <div className='nav-section'>
            <ul className={'navbar'}>
              {permi_search && (
                <li
                  className={classNames('nav-item', isActive('searchPage'))}
                  onClick={handleClickOnMenu}
                >
                  <div className='menu flex'>
                    <Link to='/search' data-testid='search-menu'>
                      <UiTooltip
                        placement='left'
                        title={
                          !isSidebarMenuExpanded ? (
                            <span>{t('layout:freeSearch')}</span>
                          ) : (
                            ''
                          )
                        }
                      >
                        <span>
                          <UiIcon icon='ri-search-line' size={20} />
                        </span>
                      </UiTooltip>
                      <label className='title'>{t('layout:freeSearch')}</label>
                    </Link>
                  </div>
                </li>
              )}
              {!!hasBrandBaanPermission && (
                <li
                  className={classNames('nav-item', isActive('brandBaan'))}
                  onClick={() => {
                    handleSubMenuState('');
                    handleClickOnMenu();
                  }}
                >
                  <div className='menu flex'>
                    <Link to='/brandbaan' data-testid='brandbaan-menu'>
                      <UiTooltip
                        placement='left'
                        title={
                          !isSidebarMenuExpanded ? (
                            <span>{t('common:brandBaan')}</span>
                          ) : (
                            ''
                          )
                        }
                      >
                        <span>
                          <UiIcon icon='ri-vip-diamond-line' size={20} />
                        </span>
                      </UiTooltip>
                      <label className='title'>{t('common:brandBaan')}</label>
                    </Link>
                  </div>
                </li>
              )}

              {hasEcommercePermission && Boolean(false) && (
                <li
                  className={classNames(
                    'nav-item',
                    'content-sub-menu',
                    isActive('ecommerce')
                  )}
                  onClick={() => {
                    handleSubMenuState('');
                    handleClickOnMenu();
                  }}
                >
                  <div className='menu flex'>
                    <Link
                      to={'/ecommerce/digikala'}
                      data-testid='ecommerce-menu'
                    >
                      <UiTooltip
                        placement='left'
                        title={
                          !isSidebarMenuExpanded ? (
                            <span>{t('ecommerce:menuTitle')}</span>
                          ) : (
                            ''
                          )
                        }
                      >
                        <span>
                          <UiIcon icon='ri-shopping-basket-2-line' size={20} />
                        </span>
                      </UiTooltip>
                      <label className='title'>
                        {t('ecommerce:menuTitle')}
                      </label>
                    </Link>
                  </div>
                </li>
              )}

              {(permi_search ||
                hasMultiPlatformPermission ||
                hasNewsPermission ||
                hasTwitterPermission ||
                hasInstagramPermission ||
                hasInstagramCommentPermission ||
                hasTelegramPermission ||
                hasNewspaperPermission ||
                hasNativePlatformsPermission) && (
                <li
                  className={classNames(
                    'nav-item',
                    'content-sub-menu',
                    isActive('newsAndInformation')
                  )}
                  onClick={() => {
                    handleSubMenuState('');
                    handleClickOnMenu();
                  }}
                >
                  <div className='menu flex'>
                    <Link
                      to={`/contents/${
                        lastSelectedPlatform || getSettingsDefaultPlatform()
                      }`}
                      data-testid='content-menu'
                    >
                      <UiTooltip
                        placement='left'
                        title={
                          !isSidebarMenuExpanded ? (
                            <span>{t('content:contents')}</span>
                          ) : (
                            ''
                          )
                        }
                      >
                        <span>
                          <UiIcon icon='ri-file-list-2-line' size={20} />
                        </span>
                      </UiTooltip>
                      <label className='title'>{t('content:contents')}</label>
                    </Link>
                  </div>
                </li>
              )}

              {(permi_search ||
                hasNewsPermission ||
                hasTwitterPermission ||
                hasInstagramPermission ||
                hasInstagramCommentPermission ||
                hasTelegramPermission ||
                hasNewspaperPermission ||
                hasNativePlatformsPermission) && (
                <li
                  className={classNames(
                    'nav-item',
                    'statistics-sub-menu',
                    isActive('statisticsAndCharts')
                  )}
                  onClick={() => {
                    handleSubMenuState('');
                    handleClickOnMenu();
                  }}
                >
                  <div className='menu flex'>
                    <Link
                      to={`/statistics/${statisticsPlatform}`}
                      data-testid='statistics-menu'
                    >
                      <UiTooltip
                        placement='left'
                        title={
                          !isSidebarMenuExpanded ? (
                            <span>{t('statistics:pageTitle')}</span>
                          ) : (
                            ''
                          )
                        }
                      >
                        <span>
                          <UiIcon icon='ri-pie-chart-line' size={20} />
                        </span>
                      </UiTooltip>
                      <label className='title'>
                        {t('statistics:pageTitle')}
                      </label>
                    </Link>
                  </div>
                </li>
              )}

              {!!hasDashboardPermission && !!hasDashboardMenuPermission && (
                <li
                  className={classNames('nav-item', isActive('dashboard'))}
                  onClick={() => {
                    handleSubMenuState('');
                    handleClickOnMenu();
                  }}
                >
                  <div className='menu flex'>
                    <Link to='/dashboard' data-testid='dashboard-menu'>
                      <UiTooltip
                        placement='left'
                        title={
                          !isSidebarMenuExpanded ? (
                            <span>{t('common:dashboard')}</span>
                          ) : (
                            ''
                          )
                        }
                      >
                        <span>
                          <UiIcon icon='ri-broadcast-line' size={20} />
                        </span>
                      </UiTooltip>
                      <label className='title'>{t('common:dashboard')}</label>
                    </Link>
                  </div>
                </li>
              )}

              {/* موضوعات */}
              <li
                className={classNames('nav-item', isActive('subject'))}
                onClick={() => {
                  handleSubMenuState('');
                  handleClickOnMenu();
                }}
              >
                <div className='menu flex'>
                  <Link to='/subjects' data-testid='subject-menu'>
                    <UiTooltip
                      placement='left'
                      title={
                        !isSidebarMenuExpanded ? (
                          <span>{t('subject:subjects')}</span>
                        ) : (
                          ''
                        )
                      }
                    >
                      <span>
                        <UiIcon icon='ri-focus-2-line' size={20} />
                      </span>
                    </UiTooltip>
                    <label className='title'>{t('subject:yourSubjects')}</label>
                  </Link>
                </div>
              </li>

              {(permi_bulletin_list || permi_bulletin_config) && (
                <li className='nav-item'>
                  <div
                    className={classNames(
                      'menu flex',
                      isActive(
                        'bulletinList',
                        'bulletinSettings',
                        'bulletinArchive'
                      )
                    )}
                    onClick={() => {
                      handleSubMenuState('bulletin');
                      if (!isSidebarMenuExpanded) {
                        handleToggleMenu();
                      }
                    }}
                    data-testid='bulletin-parent-menu'
                  >
                    <UiTooltip
                      placement='left'
                      title={
                        !isSidebarMenuExpanded ? (
                          <span>{t('layout:bulletin')}</span>
                        ) : (
                          ''
                        )
                      }
                    >
                      <span>
                        <UiIcon icon='ri-folder-open-line' size={20} />
                      </span>
                    </UiTooltip>
                    <label className='title'>{t('layout:bulletin')}</label>
                    <UiIcon
                      size={20}
                      icon='ri-arrow-down-s-line'
                      className={classNames(
                        'arrow-down',
                        isSubMenuOpen('bulletin') ? 'open' : ''
                      )}
                    />
                  </div>
                  {isSidebarMenuExpanded && isSubMenuOpen('bulletin') && (
                    <ul className='navbar'>
                      {permi_bulletin_list && (
                        <li
                          className={classNames(
                            'sub-menu',
                            isActive('bulletinList')
                          )}
                          onClick={handleClickOnMenu}
                        >
                          <Link to='/bulletins' data-testid='bulletin-menu'>
                            <div className='menu flex'>
                              <label className='title'>
                                {t('layout:bulletinList')}
                              </label>
                            </div>
                          </Link>
                        </li>
                      )}
                      {permi_bulletin_config && (
                        <li
                          className={classNames(
                            'sub-menu',
                            isActive('bulletinSettings')
                          )}
                          onClick={handleClickOnMenu}
                        >
                          <Link
                            to='/bulletin-settings'
                            data-testid='bulletin-settings-menu'
                          >
                            <div className='menu flex'>
                              <label className='title'>
                                {t('layout:bulletinSettings')}
                              </label>
                            </div>
                          </Link>
                        </li>
                      )}
                      {permi_bulletin_list && (
                        <li
                          className={classNames(
                            'sub-menu',
                            isActive('bulletinArchive')
                          )}
                          onClick={handleClickOnMenu}
                        >
                          <Link
                            to='/bulletin-archive'
                            data-testid='bulletin-archive-menu'
                          >
                            <div className='menu flex'>
                              <label className='title'>
                                {t('layout:bulletinArchive')}
                              </label>
                            </div>
                          </Link>
                        </li>
                      )}
                    </ul>
                  )}
                </li>
              )}

              {/* آلارم */}
              {permi_alarm_list && (
                <li className='nav-item'>
                  <Link
                    to='/alarms'
                    className={classNames('menu flex', isActive('alarm'))}
                    onClick={() => {
                      if (!isSidebarMenuExpanded) {
                        handleToggleMenu();
                      }
                    }}
                    data-testid='alarm-parent-menu'
                  >
                    <UiTooltip
                      placement='left'
                      title={
                        !isSidebarMenuExpanded ? (
                          <span>{t('layout:alarm')}</span>
                        ) : (
                          ''
                        )
                      }
                    >
                      <span>
                        <UiIcon icon='ri-alarm-warning-line' size={20} />
                      </span>
                    </UiTooltip>
                    <label className='title'>{t('alarm:alarm')}</label>
                  </Link>
                </li>
              )}

              {permi_searchTag_index && (
                <li
                  className={classNames('nav-item', isActive('resources'))}
                  onClick={() => {
                    handleSubMenuState('');
                    handleClickOnMenu();
                  }}
                >
                  <div className='menu flex'>
                    <Link
                      to='/resource-categories'
                      data-testid='resources-menu'
                    >
                      <UiTooltip
                        placement='left'
                        title={
                          !isSidebarMenuExpanded ? (
                            <span>{t('layout:resources')}</span>
                          ) : (
                            ''
                          )
                        }
                      >
                        <span>
                          <UiIcon icon='ri-file-list-2-line' size={20} />
                        </span>
                      </UiTooltip>
                      <label className='title'>{t('layout:resources')}</label>
                    </Link>
                  </div>
                </li>
              )}

              {permi_comparison && (
                <li className='nav-item'>
                  <div
                    className={classNames(
                      'menu flex comparison-sub-menu',
                      isActive(
                        'comparisonList',
                        'comparisonAdd',
                        'ComparisonViewSubject',
                        'ComparisonViewSource'
                      )
                    )}
                    onClick={() => {
                      handleSubMenuState('comparison');
                      if (!isSidebarMenuExpanded) {
                        handleToggleMenu();
                      }
                    }}
                    data-testid='comparison-parent-menu'
                  >
                    <UiTooltip
                      placement='left'
                      title={
                        !isSidebarMenuExpanded ? (
                          <span>{t('comparison:comparison')}</span>
                        ) : (
                          ''
                        )
                      }
                    >
                      <span>
                        <UiIcon icon='ri-scales-line' size={20} />
                      </span>
                    </UiTooltip>
                    <label className='title'>
                      {t('comparison:comparison')}
                    </label>
                    <UiIcon
                      size={20}
                      icon='ri-arrow-down-s-line'
                      className={classNames(
                        'arrow-down',
                        isSubMenuOpen('comparison') ? 'open' : ''
                      )}
                    />
                  </div>
                  {isSidebarMenuExpanded && isSubMenuOpen('comparison') && (
                    <ul className='navbar'>
                      {permi_comparison && (
                        <li
                          className={classNames(
                            'sub-menu',
                            isActive('comparisonAdd')
                          )}
                          onClick={handleClickOnMenu}
                        >
                          <Link
                            to='/comparisons/add'
                            data-testid='comparison-add-menu'
                          >
                            <div className='menu flex'>
                              <label className='title'>
                                {t('comparison:newComparison')}
                              </label>
                            </div>
                          </Link>
                        </li>
                      )}
                      {permi_comparison && (
                        <li
                          className={classNames(
                            'sub-menu',
                            isActive('comparisonList')
                          )}
                          onClick={handleClickOnMenu}
                        >
                          <Link to='/comparisons' data-testid='comparison-menu'>
                            <div className='menu flex'>
                              <label className='title'>
                                {t('comparison:comparisonList')}
                              </label>
                            </div>
                          </Link>
                        </li>
                      )}
                    </ul>
                  )}
                </li>
              )}
              {permi_tag_index && (
                <li
                  className={classNames('nav-item', isActive('tags'))}
                  onClick={() => {
                    handleSubMenuState('');
                    handleClickOnMenu();
                  }}
                >
                  <div className='menu flex'>
                    <Link to='/tags' data-testid='tags-menu'>
                      <UiTooltip
                        placement='left'
                        title={
                          !isSidebarMenuExpanded ? (
                            <span>{t('tag:tags')}</span>
                          ) : (
                            ''
                          )
                        }
                      >
                        <span>
                          <UiIcon icon='ri-hashtag' size={20} />
                        </span>
                      </UiTooltip>
                      <label className='title'>{t('tag:tag')}</label>
                    </Link>
                  </div>
                </li>
              )}
              {!isArabicLanguage && (
                <li
                  className={classNames('nav-item', isActive('kiosk'))}
                  onClick={() => {
                    handleSubMenuState('');
                    handleClickOnMenu();
                  }}
                >
                  <div className='menu flex'>
                    <Link to='/kiosk' data-testid='kiosk-menu'>
                      <UiTooltip
                        placement='left'
                        title={
                          !isSidebarMenuExpanded ? (
                            <span>{t('layout:kiosk')}</span>
                          ) : (
                            ''
                          )
                        }
                      >
                        <span>
                          <UiIcon icon='ri-newspaper-line' size={20} />
                        </span>
                      </UiTooltip>
                      <label className='title'>{t('layout:kiosk')}</label>
                    </Link>
                  </div>
                </li>
              )}
              {hasUserListPermission && !isRootUser() ? (
                <li
                  className={classNames('nav-item', isActive('userManagement'))}
                  onClick={() => {
                    handleSubMenuState('');
                    handleClickOnMenu();
                  }}
                >
                  <div className='menu flex'>
                    <Link
                      to='/users-management'
                      data-testid='users-management-menu'
                    >
                      <UiTooltip
                        placement='left'
                        title={
                          !isSidebarMenuExpanded ? (
                            <span>{t('users:userManagement')}</span>
                          ) : (
                            ''
                          )
                        }
                      >
                        <span>
                          <UiIcon icon='ri-user-line' size={20} />
                        </span>
                      </UiTooltip>
                      <label className='title'>
                        {t('users:userManagement')}
                      </label>
                    </Link>
                  </div>
                </li>
              ) : (
                <></>
              )}
              {(permi_admin_users ||
                permi_admin_support ||
                permi_admin_vip_permission) && (
                <li className='nav-item'>
                  <div
                    className={classNames(
                      'menu flex',
                      isActive(
                        'adminUsers',
                        'adminSupport',
                        'adminSupportCategory',
                        'adminVipPermission'
                      )
                    )}
                    onClick={() => {
                      handleSubMenuState('admin');
                      if (!isSidebarMenuExpanded) {
                        handleToggleMenu();
                      }
                    }}
                    data-testid='admin-parent-menu'
                  >
                    <UiTooltip
                      placement='left'
                      title={
                        !isSidebarMenuExpanded ? (
                          <span>{t('layout:systemManagement')}</span>
                        ) : (
                          ''
                        )
                      }
                    >
                      <span>
                        <UiIcon icon='ri-admin-line' size={20} />
                      </span>
                    </UiTooltip>
                    <label className='title'>
                      {t('layout:systemManagement')}
                    </label>
                    <UiIcon
                      size={20}
                      icon='ri-arrow-down-s-line'
                      className={classNames(
                        'arrow-down',
                        isSubMenuOpen('admin') ? 'open' : ''
                      )}
                    />
                  </div>
                  {isSidebarMenuExpanded && isSubMenuOpen('admin') && (
                    <ul className='navbar'>
                      {permi_admin_users && (
                        <li
                          className={classNames(
                            'sub-menu',
                            isActive('adminUsers')
                          )}
                          onClick={handleClickOnMenu}
                        >
                          <Link to='/users' data-testid='users-menu'>
                            <div className='menu flex'>
                              <label className='title'>
                                {t('users:users')}
                              </label>
                            </div>
                          </Link>
                        </li>
                      )}

                      {Boolean(hasCompanyListPermission) && (
                        <li
                          className={classNames(
                            'sub-menu',
                            isActive('companies')
                          )}
                          onClick={handleClickOnMenu}
                        >
                          <Link to='/companies' data-testid='companies-menu'>
                            <div className='menu flex'>
                              <label className='title'>
                                {t('companies:companyInfo')}
                              </label>
                            </div>
                          </Link>
                        </li>
                      )}

                      {permi_admin_support && (
                        <li
                          className={classNames(
                            'sub-menu',
                            isActive('adminSupport')
                          )}
                          onClick={handleClickOnMenu}
                        >
                          <Link to='/tickets' data-testid='admin-support-menu'>
                            <div className='menu flex'>
                              <label className='title'>
                                {t('support:tickets')}
                              </label>
                            </div>
                          </Link>
                        </li>
                      )}

                      {permi_admin_support && (
                        <li
                          className={classNames(
                            'sub-menu',
                            isActive('adminSupportCategory')
                          )}
                          onClick={handleClickOnMenu}
                        >
                          <Link
                            to='/ticket-categories'
                            data-testid='admin-support-category-menu'
                          >
                            <div className='menu flex'>
                              <label className='title'>
                                {t('support:ticketsCategory')}
                              </label>
                            </div>
                          </Link>
                        </li>
                      )}

                      {hasBrandBuilderPermission && (
                        <li
                          className={classNames(
                            'sub-menu',
                            isActive('brandBuilder')
                          )}
                          onClick={handleClickOnMenu}
                        >
                          <Link
                            to='/brandBuilder'
                            data-testid='brand-builder-menu'
                          >
                            <div className='menu flex'>
                              <label className='title'>
                                {t('layout:brandBuilder')}
                              </label>
                            </div>
                          </Link>
                        </li>
                      )}

                      {hasBrandBuilderPermission && (
                        <li
                          className={classNames(
                            'sub-menu',
                            isActive('industryBuilder')
                          )}
                          onClick={handleClickOnMenu}
                        >
                          <Link
                            to='/industryBuilder'
                            data-testid='industry-builder-menu'
                          >
                            <div className='menu flex'>
                              <label className='title'>
                                {t('layout:industryBuilder')}
                              </label>
                            </div>
                          </Link>
                        </li>
                      )}

                      {permi_admin_vip_permission && (
                        <li
                          className={classNames(
                            'sub-menu',
                            isActive('adminVipPermission')
                          )}
                          onClick={handleClickOnMenu}
                        >
                          <Link
                            to='/vip-resources-management'
                            data-testid='admin-vip-permission-menu'
                          >
                            <div className='menu flex'>
                              <label className='title'>
                                {t('layout:vipResourceAccess')}
                              </label>
                            </div>
                          </Link>
                        </li>
                      )}

                      {Boolean(hasTinyUrlPagePermission) && (
                        <li
                          className={classNames(
                            'sub-menu',
                            isActive('tinyUrl')
                          )}
                          onClick={handleClickOnMenu}
                        >
                          <Link to='/tiny-url' data-testid='tinyUrl-menu'>
                            <div className='menu flex'>
                              <label className='title'>
                                {t('tinyUrl:urlShortener')}
                              </label>
                            </div>
                          </Link>
                        </li>
                      )}
                    </ul>
                  )}
                </li>
              )}
            </ul>
          </div>

          {/* دستان - فیکس شده در پایین سایدبار */}
          {hasAiChatbotPermission && getEnv('REACT_APP_CHATBOT_URL') && (
            <ul className='navbar chatbot-fixed-list'>
              <li className='nav-item'>
                <div
                  className={classNames('menu flex chatbot-menu', {
                    'expanded-chatbot': isSidebarMenuExpanded,
                  })}
                >
                  <div
                    className='chatbot-btn'
                    onClick={handleChatbotMenuClick}
                    data-testid='chatbot-menu'
                  >
                    <UiTooltip
                      placement='left'
                      title={
                        !isSidebarMenuExpanded ? (
                          <span>{t('layout:chatbot')}</span>
                        ) : (
                          ''
                        )
                      }
                    >
                      <span className='chatbot-icon-wrapper'>
                        <img src={dastanLogo} alt='' className='chatbot-icon' />
                      </span>
                    </UiTooltip>
                    <div className='chatbot-text'>
                      <label className='title'>{t('layout:chatbot')}</label>
                      <span className='subtitle'>
                        {t('layout:chatbotSubtitle')}
                      </span>
                    </div>
                  </div>
                </div>
              </li>
            </ul>
          )}

          <UiConfirmDialog
            open={openTrialVersionModal}
            actions={
              <>
                <UiButton
                  variant='text'
                  style={{ width: 140 }}
                  onClick={() => setOpenTrialVersionModal(false)}
                >
                  {t('layout:neverMind')}
                </UiButton>
                <UiButton
                  variant='contained'
                  target='_blank'
                  style={{ color: 'white' }}
                  href={
                    localStorage.getItem('referrer') ||
                    getEnv('REACT_APP_BASE_URL')
                  }
                >
                  {t('layout:backToPreviouseVersion')}
                </UiButton>
              </>
            }
            onCancel={() => setOpenTrialVersionModal(false)}
            onConfirm={() => setOpenTrialVersionModal(false)}
          >
            <p>{t('layout:thisFeatureIsNotActiveInThisVersion')}</p>
          </UiConfirmDialog>
          {chatToken && getEnv('REACT_APP_ENVIRONMENT') === 'development' && (
            <ChatwootWidget />
          )}
        </div>
      </div>
    </StyledLayoutSidebar>
  );
};

export default memo(LayoutSidebar);
